"use client"

/**
 * ProductCardButtons - Client component for product card actions.
 *
 * Buy / Rent act RIGHT ON THE CARD: Buy adds to the cart (optimistic +
 * toast), Rent opens the quick-rent dialog (dates + duration). Neither
 * navigates. Out of stock → disabled "Нет в наличии". The only link is
 * "Выбрать" when the product has several variants and the card cannot
 * choose for the customer. States come from resolveCardActions (pure,
 * unit-tested) fed by the backend's in_stock_now.
 */
import * as React from "react"
import { Button } from "@/components/ui/button"
import { ShoppingCart, Clock, Loader2 } from "lucide-react"
import { useAddToCart } from "@/hooks/use-cart-mutations"
import { toast } from "@/hooks/use-toast"
import { useTranslations } from "next-intl"
import { cartErrorText } from "@lib/util/cart-error-message"
import { useParams, useRouter } from "next/navigation"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import QuickRentDialog from "@modules/products/components/quick-rent-dialog"
import type { RentalInfo } from "@/types/rental"
import { resolveCardActions } from "@lib/util/buy-actions"

interface ProductCardButtonsProps {
  productHandle: string
  productTitle?: string
  variantId?: string
  /** number of variants; >1 means the customer must choose on the page */
  variantCount?: number
  isInStock: boolean
  rentalInfo?: RentalInfo | null
  currencyCode?: string
  regionId?: string
}

export function ProductCardButtons({
  productHandle,
  productTitle,
  variantId,
  variantCount = variantId ? 1 : 0,
  isInStock,
  rentalInfo,
  currencyCode = "CZK",
  regionId,
}: ProductCardButtonsProps) {
  const [quickRentOpen, setQuickRentOpen] = React.useState(false)
  const t = useTranslations()
  const params = useParams()
  const router = useRouter()
  const countryCode = params.countryCode as string
  const addToCart = useAddToCart()

  const { buy, rent } = resolveCardActions({
    inStock: isInStock,
    variantId,
    variantCount,
    rentalInfo,
  })

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!variantId || addToCart.isPending) return

    addToCart.mutate(
      { variantId, quantity: 1, countryCode },
      {
        onSuccess: () => {
          router.refresh()
          toast({
            title: t('cart.addedToCart'),
            description: t('cart.addedToCartDesc'),
          })
        },
        onError: (error) => {
          // Prefer the localized message from cart.errors.<code> when the
          // backend tagged the failure (insufficientInventory, etc.);
          // fall back to the raw message only if we can't translate.
          toast({
            title: t('cart.error'),
            description: cartErrorText(t, error, {
              errorsKey: 'cart.errors',
              fallback: t('cart.addFailed'),
            }),
            variant: 'destructive',
          })
        },
      }
    )
  }

  // Nothing sellable or rentable on this product → plain details link
  if (buy === "hidden" && rent === "hidden") {
    return (
      <LocalizedClientLink href={`/products/${productHandle}`} className="flex-1">
        <Button variant="secondary" className="w-full">
          {t('product.viewDetails')}
        </Button>
      </LocalizedClientLink>
    )
  }

  const rentVisible = rent !== "hidden"
  const buyVariant = rentVisible ? "secondary" : "default"

  /**
   * Nothing to offer: one line, not two.
   *
   * A card whose item can be neither rented nor bought rendered BOTH refusals —
   * two identical greyed buttons side by side saying "out of stock", which reads
   * as a rendering bug rather than an answer. The state is one fact about the
   * item, so it is said once, across the whole row.
   */
  if (rent === "outOfStock" && buy === "outOfStock") {
    return (
      <div className="flex w-full">
        <Button
          variant="secondary"
          size="sm"
          className="flex-1"
          disabled
          aria-disabled="true"
          data-testid="card-out-of-stock"
          data-state="out-of-stock"
        >
          {t('product.actions.outOfStock')}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex gap-2 w-full">
      {/* Rent — quick-rent dialog right here (dates + duration), never a page hop */}
      {rent === "dialog" && rentalInfo && (
        <Button
          variant="default"
          className="flex-1"
          size="sm"
          data-testid="card-rent-button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setQuickRentOpen(true)
          }}
        >
          <Clock className="mr-1.5 h-3.5 w-3.5" />
          {t('product.rental.rent')}
        </Button>
      )}
      {rent === "outOfStock" && (
        <Button
          variant="default"
          className="flex-1"
          size="sm"
          disabled
          aria-disabled="true"
          data-testid="card-rent-button"
          data-state="out-of-stock"
        >
          {t('product.actions.outOfStock')}
        </Button>
      )}

      {/* Buy — add to cart right here; disabled when out of stock */}
      {buy === "add" && (
        <Button
          onClick={handleAddToCart}
          disabled={addToCart.isPending}
          variant={buyVariant}
          size="sm"
          className="flex-1"
          data-testid="card-buy-button"
          data-state="add"
        >
          {addToCart.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <>
              <ShoppingCart className="mr-1.5 h-3.5 w-3.5" />
              {t('product.actions.buy')}
            </>
          )}
        </Button>
      )}
      {buy === "outOfStock" && (
        <Button
          variant={buyVariant}
          size="sm"
          className="flex-1"
          disabled
          aria-disabled="true"
          data-testid="card-buy-button"
          data-state="out-of-stock"
        >
          {t('product.actions.outOfStock')}
        </Button>
      )}
      {/* The ONE case that leaves the card: several variants, customer must pick */}
      {buy === "choose" && (
        <LocalizedClientLink
          href={`/products/${productHandle}`}
          className="flex-1"
          data-testid="card-choose-link"
        >
          <Button variant={buyVariant} size="sm" className="w-full">
            {t('product.actions.choose')}
          </Button>
        </LocalizedClientLink>
      )}

      {/* Quick rent dialog — тот же RentalSection что и на странице
          деталей, с календарём и проверкой доступности */}
      {rent === "dialog" && rentalInfo && (
        <QuickRentDialog
          open={quickRentOpen}
          onOpenChange={setQuickRentOpen}
          productHandle={productHandle}
          productTitle={productTitle || productHandle}
          variantIdHint={variantId}
          rentalInfo={rentalInfo}
          regionId={regionId}
        />
      )}
    </div>
  )
}

export default ProductCardButtons
